import { useEffect, useRef, useState, type RefObject } from 'react';

/*
 * Высота формы операции берётся один раз, при открытии, и не пересчитывается
 * под системную клавиатуру: иначе форма прыгала бы при каждом касании поля.
 *
 * Но и `innerHeight` одного мало. В браузере с панелью снизу видимая область
 * меньше окна, и лист, построенный от окна, уходил бы под панель вместе с
 * кнопкой записи. Поэтому берётся меньшее из двух — с одной оговоркой:
 * разница больше 120 px означает уже открытую клавиатуру, и тогда мерить по
 * ней нельзя, иначе форма родится с высотой в треть экрана.
 */
const layoutHeight = (): number => {
  if (typeof window === 'undefined') return 800;
  const v = window.visualViewport;
  const seen = v ? v.height + v.offsetTop : window.innerHeight;
  return window.innerHeight - seen > 120 ? window.innerHeight : Math.min(window.innerHeight, seen);
};

export function useOperationKeyboard() {
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const amountRef = useRef<HTMLButtonElement>(null);
  const [editingNote, setEditingNote] = useState(false);
  const [height, setHeight] = useState(layoutHeight);
  const [keyboardInset, setKeyboardInset] = useState(0);
  /** Пишут заметку, и системная клавиатура открыта: лист встаёт прямо на неё. */
  const [typing, setTyping] = useState(false);
  const baseline = useRef(height);
  const seenKeyboard = useRef(false);
  const returnToAmount = () => {
    noteRef.current?.blur(); setEditingNote(false);
    amountRef.current?.focus({ preventScroll: true });
  };
  useEffect(() => {
    const viewport = window.visualViewport;
    let frame = 0; let orientationTimer: ReturnType<typeof setTimeout>;
    const update = () => {
      // Pinch-to-zoom is a user action, never interpreted as a software keyboard.
      if (viewport && Math.abs(viewport.scale - 1) > 0.05) return;
      const seen = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
      // Открыта ли клавиатура — по высоте, измеренной при открытии листа.
      const open = baseline.current - seen > 120;
      /*
        Сколько клавиатура закрывает внутри нынешнего окна. Приложение с
        экрана «Домой» iPhone уменьшает целиком — и тогда это ноль: вычесть
        клавиатуру ещё раз значило оставить форме треть места и пустоту под
        ней (16.10.2026).
      */
      const inset = Math.max(0, window.innerHeight - seen);
      /*
        Место под системную клавиатуру — только пока пишут заметку: в
        остальное время на экране своя клавиатура SKOKA. iPhone после свайпа
        по листу сообщал о «клавиатуре», которой не было, и лист отрезал под
        неё низ — с цифрами (15.10.2026).
      */
      const writing = open && document.activeElement === noteRef.current;
      setKeyboardInset(writing ? inset : 0);
      setTyping(writing);
      if (document.activeElement === noteRef.current) {
        if (open) { seenKeyboard.current = true;
          cancelAnimationFrame(frame); frame = requestAnimationFrame(() => {
            const input = noteRef.current; const scroll = input?.closest('.opcontent, .operation-scroll');
            if (!input || !scroll || !viewport) return;
            const bottom = Math.min(viewport.height + viewport.offsetTop - 16, scroll.getBoundingClientRect().bottom - 12);
            const delta = input.getBoundingClientRect().bottom - bottom;
            if (delta > 0) scroll.scrollTop += delta;
          });
        } else if (seenKeyboard.current) { seenKeyboard.current = false; returnToAmount(); }
      } else if (!open) seenKeyboard.current = false;
    };
    const orient = () => { clearTimeout(orientationTimer); orientationTimer = setTimeout(() => { if (document.activeElement !== noteRef.current) { const next = layoutHeight(); baseline.current = next; setHeight(next); update(); } }, 300); };
    viewport?.addEventListener('resize', update); viewport?.addEventListener('scroll', update);
    window.addEventListener('orientationchange', orient);
    return () => { clearTimeout(orientationTimer); cancelAnimationFrame(frame); viewport?.removeEventListener('resize', update); viewport?.removeEventListener('scroll', update); window.removeEventListener('orientationchange', orient); };
  }, []);
  return { noteRef, amountRef, editingNote, height, keyboardInset, typing, returnToAmount,
    focusNote: () => { seenKeyboard.current = false; setEditingNote(true); },
    blurNote: () => { setEditingNote(false); setKeyboardInset(0); setTyping(false); } };
}

/** Клавиша клавиатуры компьютера → кнопка клавиатуры SKOKA. */
const PAD_KEYS: Record<string, string> = { ',': ',', '.': ',', '+': '+', '-': '-', '*': '*', '/': '/', Backspace: '⌫' };

/*
 * На компьютере сумма набирается и с клавиатуры (22.10.2026,
 * docs/design/desktop-2026-10-22): цифры, запятая или точка, + − × ÷,
 * Backspace стирает, Enter записывает. Только пока лист сверху (фокус в нём),
 * своя клавиатура на экране и не пишут в поле — заметку, поиск.
 *
 * Нажатие кнопки лист кладёт в возвращённую ссылку на каждом показе: хук
 * стоит до ранних `return` листа, а `tap` объявлен ниже. Enter нажимает
 * кнопку записи (`data-pad-enter`) — и не нажимает, пока она недоступна.
 */
export function usePadKeys(amountRef: RefObject<HTMLElement | null>) {
  const latest = useRef<(k: string) => void>(() => {});
  useEffect(() => {
    // `:focus-visible` не годится: браузер включает его самим нажатием Enter.
    let tabbed = false;
    const onPointer = () => { tabbed = false; };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Tab') { tabbed = true; return; }
      if (e.ctrlKey || e.metaKey || e.altKey || e.isComposing) return;
      const sheet = amountRef.current?.closest('.sheet');
      const pad = sheet?.querySelector<HTMLElement>('.pad');
      const at = document.activeElement;
      if (!sheet || !pad || pad.getBoundingClientRect().height < 40 || !(at && sheet.contains(at))) return;
      if (at instanceof HTMLInputElement || at instanceof HTMLTextAreaElement || at instanceof HTMLSelectElement
        || (at instanceof HTMLElement && at.isContentEditable)) return;
      if (e.key === 'Enter') {
        // Дошли до кнопки листа клавишей Tab — Enter нажимает её саму. Фокус
        // после мыши (выбрали категорию, лист вернул его на строку) — запись.
        if (at instanceof HTMLButtonElement && at !== amountRef.current && !pad.contains(at) && tabbed) return;
        e.preventDefault();
        const go = sheet.querySelector<HTMLButtonElement>('button[data-pad-enter]');
        if (go && !go.disabled) go.click();
        return;
      }
      const k = /^[0-9]$/.test(e.key) ? e.key : PAD_KEYS[e.key];
      if (!k) return;
      e.preventDefault();
      latest.current(k);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer, true);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('pointerdown', onPointer, true); };
  }, [amountRef]);
  return latest;
}
